import type { OptionalPromise } from '@adornis/base/utilTypes.js';
import type { BaseQLMiddleware } from '@adornis/baseql/metadata/types.js';
import { context } from '@adornis/baseql/server/context.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { getMyself } from '@adornis/users/db/api/currentUserInfo.operations.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import type { Person } from '../../users/entities/person.js';
import type { Permission } from '../roles.js';

export interface AuthenticatedMiddlewareContextOptions {
  user: NarrowedEntity<Person, { roles: { name: 1; contexts: 1; permissions: 1 } }>;
}

/**
 * Checks if the user has permission to call a specific baseql operation
 * @param permission the permission to check
 * @param permissionContext the context of the permission to check. can be a function to retrieve the context based on the function call. Default: ANY_CONTEXT
 * @throws an error of the user has not the right permission
 */
export const checkPermissionMiddleware =
  (
    permission: string,
    permissionContext:
      string | ((options: AuthenticatedMiddlewareContextOptions) => OptionalPromise<string>) = ANY_CONTEXT,
  ): BaseQLMiddleware =>
  async options => {
    if (context.serverContext) return options.next();
    const user = await getMyself<Person>()({ roles: { name: 1, contexts: 1, permissions: 1 } });
    if (!user) {
      throw new ExpectedError('Not authenticated');
    }

    const actualContext =
      typeof permissionContext === 'function'
        ? await permissionContext({
            user,
          } satisfies AuthenticatedMiddlewareContextOptions)
        : permissionContext;

    if (!user.hasPermission(permission as Permission, actualContext)) {
      throw new ExpectedError(`Permission for the call ${options.field.name} is not granted for user ${user._id}`);
    }
    return options.next();
  };

export const checkPermissionMiddlewareFunction =
  (
    hasPermission: (
      user: NarrowedEntity<Person, { _id: 1; roles: { name: 1; contexts: 1; permissions: 1 } }>,
      args: Record<string, any>,
    ) => OptionalPromise<boolean>,
  ): BaseQLMiddleware =>
  async options => {
    if (context.serverContext) return options.next();
    const user = await getMyself<Person>()({ _id: 1, roles: { name: 1, contexts: 1, permissions: 1 } });
    if (!user) {
      throw new ExpectedError('Not authenticated');
    }

    if (!(await hasPermission(user, options.args))) {
      throw new ExpectedError(`Permission for the call ${options.field.name} is not granted for user ${user._id}`);
    }
    return options.next();
  };
